// Passkeys (WebAuthn) in the browser: Face ID / Touch ID / fingerprint / Windows Hello. The server sends the options
// as JSON with base64url values; the browser API wants ArrayBuffers (and returns them), so we convert both ways here
// instead of relying on PublicKeyCredential.parse*FromJSON, which older iPhones do not have.

const fromB64url = (s) => {
    const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4);
    return Uint8Array.from(atob(b64), c => c.charCodeAt(0)).buffer;
};

const toB64url = (buf) => {
    let bin = '';
    new Uint8Array(buf).forEach(b => { bin += String.fromCharCode(b); });
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/** True when this browser can use passkeys (secure context + WebAuthn). */
export const passkeysSupported = () =>
    typeof window !== 'undefined' && window.isSecureContext && !!window.PublicKeyCredential && !!navigator.credentials;

/** Runs the "create passkey" ceremony; returns the credential as JSON for the server. */
export async function createPasskey(optionsJson) {
    const { publicKey } = JSON.parse(optionsJson);
    publicKey.challenge = fromB64url(publicKey.challenge);
    publicKey.user.id = fromB64url(publicKey.user.id);
    publicKey.excludeCredentials = (publicKey.excludeCredentials || []).map(c => ({ ...c, id: fromB64url(c.id) }));
    const cred = await navigator.credentials.create({ publicKey });
    return JSON.stringify({
        id: cred.id,
        rawId: toB64url(cred.rawId),
        type: cred.type,
        response: {
            attestationObject: toB64url(cred.response.attestationObject),
            clientDataJSON: toB64url(cred.response.clientDataJSON),
            transports: cred.response.getTransports ? cred.response.getTransports() : [],
        },
        clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
    });
}

/** Runs the "sign in with passkey" ceremony; returns the assertion as JSON for the server. */
export async function getPasskey(optionsJson) {
    const { publicKey } = JSON.parse(optionsJson);
    publicKey.challenge = fromB64url(publicKey.challenge);
    publicKey.allowCredentials = (publicKey.allowCredentials || []).map(c => ({ ...c, id: fromB64url(c.id) }));
    const cred = await navigator.credentials.get({ publicKey });
    return JSON.stringify({
        id: cred.id,
        rawId: toB64url(cred.rawId),
        type: cred.type,
        response: {
            authenticatorData: toB64url(cred.response.authenticatorData),
            clientDataJSON: toB64url(cred.response.clientDataJSON),
            signature: toB64url(cred.response.signature),
            userHandle: cred.response.userHandle ? toB64url(cred.response.userHandle) : null,
        },
        clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
    });
}

/** A readable label for the device a passkey is created on ("iPhone", "Mac", "Android"…). */
export function deviceName() {
    const ua = navigator.userAgent;
    if (/iPhone/.test(ua)) return 'iPhone';
    if (/iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'iPad';
    if (/Android/.test(ua)) return 'Android';
    if (/Macintosh/.test(ua)) return 'Mac';
    if (/Windows/.test(ua)) return 'Windows';
    if (/Linux/.test(ua)) return 'Linux';
    return 'Passkey';
}

/** The user closed the Face ID / passkey prompt (not an error worth showing). */
export const isCancelled = (err) => err?.name === 'NotAllowedError' || err?.name === 'AbortError';
